import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import PhoneInput from 'react-phone-input-2';
import RichTextEditor from 'react-rte';
import { ReactComponent as IconPassword } from 'assets/iconButtons/eye.svg';
import { ReactComponent as IconPasswordOff } from 'assets/iconButtons/eye-off.svg';
import { ReactComponent as IconError } from 'assets/iconButtons/warning.svg';
import { classNames } from '../utils';
import { convertHtmlToString } from 'components/Messages/utils';
import { sanitizeMessageText } from 'services/utils';
import './Input.scss';

const toolbarConfig = {
  display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'LINK_BUTTONS'],
  INLINE_STYLE_BUTTONS: [
    { label: 'Bold', style: 'BOLD', className: 'custom-css-class' },
    { label: 'Italic', style: 'ITALIC' },
    { label: 'Underline', style: 'UNDERLINE' }
  ],
  BLOCK_TYPE_BUTTONS: [
    { label: 'UL', style: 'unordered-list-item' },
    { label: 'OL', style: 'ordered-list-item' }
  ]
};

const Input = ({
  type = 'text',
  name,
  error,
  isApiError,
  value,
  setValue,
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  label,
  placeholder,
  subText,
  symbolsCountText,
  className,
  Icon,
  initialStatus,
  isDisabled,
  autoComplete = 'on',
  isRequired,
  autoFocus = false,
  max,
  rows,
  setCountryCode,
  excludeCountries = [],
  inputRef
}) => {
  const [inputType, setInputType] = useState(type);
  const editorContainerRef = useRef();

  const handleBeforeInputRichTextEditor = () => {
    if (convertHtmlToString(value).length >= max) {
      return 'handled';
    }

    // Let RichTextEditor handle normal input
    return 'not-handled';
  };

  const handlePastTextRichTextEditor = (pastedText) => {
    // Sanitize pasted text to remove problematic characters
    const sanitizedText = sanitizeMessageText(pastedText);
    const totalLength = convertHtmlToString(value).length + sanitizedText.length;
    if (totalLength > max) {
      return 'handled';
    }

    // Return sanitized text to let RichTextEditor handle the insertion
    // This preserves cursor position and formatting
    return sanitizedText;
  };

  const handleRichTextEditorFix = (e) => {
    e.preventDefault();

    // Get current content and sanitize it
    const currentText = convertHtmlToString(value);
    const sanitizedText = sanitizeMessageText(currentText);

    // Update the editor with sanitized content
    const newValue = RichTextEditor.createValueFromString(sanitizedText, 'markdown');
    onChange(newValue);
  };

  const toggleShowingPassword = () => {
    setInputType(inputType === 'password' ? 'text' : 'password');
  };

  const inputChange = (e) => {
    if (name === 'comment' && e.target.value.startsWith(' ') && e.target.value.trim() === '') {
      onChange({ ...e, target: { ...e.target, value: '' } });
    } else {
      onChange(e);
    }
  };

  const handleBlur = (e) => onBlur(e);

  const handleFocus = () => onFocus();

  const handleLinkClick = (e) => {
    if (isDisabled) {
      e.preventDefault();
    }
  };

  const handleEnterPress = (e) => {
    if (e.key === 'Enter' && isDisabled) {
      e.preventDefault();
    }
  };

  const inputClasses = classNames({
    'input-wrapper': true,
    error: !!error || !!isApiError,
    hit: !error && !isApiError && !!initialStatus,
    active: value?.length,
    disabled: isDisabled,
    icon: Icon || type === 'password' || error,
    [className]: true
  });

  const renderIcons = () => {
    if (Icon) {
      return <Icon className={'input-icon'} />;
    }

    if (type === 'password') {
      return (
        <div
          role={'button'}
          tabIndex={0}
          onKeyDown={toggleShowingPassword}
          onClick={toggleShowingPassword}
          className={`input-icon eye ${error ? 'error' : ''}`}
        >
          {inputType === 'text' ? <IconPassword /> : <IconPasswordOff />}
          {error && <IconError />}
        </div>
      );
    }

    return error && <IconError className={'input-icon'} />;
  };

  return type === 'phone' ? (
    <div className={inputClasses}>
      <PhoneInput
        /* eslint-disable-next-line max-len */
        containerClass={`custom-react-tel-input ${(error || isApiError) && 'invalid-tel-number'} ${
          !error && !isApiError && !!initialStatus && 'hit-tel-number'
        }`}
        country={process.env.REACT_APP_DEFAULT_COUNTRY_CODE.toLowerCase()}
        excludeCountries={excludeCountries}
        specialLabel={label + (isRequired ? '*' : '')}
        inputProps={{ name, required: isRequired, autoFocus }}
        copyNumbersOnly={false}
        value={value}
        onBlur={onBlur}
        onChange={(phoneNumber, countryData, e, formattedValue) => {
          if (e.type === 'click') {
            setValue(name, formattedValue);
          } else {
            onChange(e);
          }
          setCountryCode(countryData?.countryCode);
        }}
      />
      {(error || subText || symbolsCountText) && (
        <div
          // eslint-disable-next-line max-len
          className={`input-under-text-wrapper ${!error && !subText && symbolsCountText ? 'justify-content-flex-end' : ''}`}
        >
          {(error || subText) && <p className={`input-sub-text ${error ? 'input-error' : ''}`}>{error || subText}</p>}
          {symbolsCountText && <p className={'input-symbols-count-text'}>{symbolsCountText}</p>}
        </div>
      )}
    </div>
  ) : type === 'textarea' ? (
    <div className={inputClasses}>
      {label && <label htmlFor={name}>{label + (isRequired ? '*' : '')}</label>}
      <textarea
        name={name}
        value={value || ''}
        onChange={inputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={isDisabled}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        maxLength={max}
        rows={rows}
        className={'input'}
      />
      {renderIcons()}
      {(error || subText || symbolsCountText) && (
        <div
          // eslint-disable-next-line max-len
          className={`input-under-text-wrapper ${!error && !subText && symbolsCountText ? 'justify-content-flex-end' : ''}`}
        >
          {(error || subText) && <p className={`input-sub-text ${error ? 'input-error' : ''}`}>{error || subText}</p>}
          {symbolsCountText && <p className={'input-symbols-count-text'}>{symbolsCountText}</p>}
        </div>
      )}
    </div>
  ) : type === 'text-editor' ? (
    <div
      className={'text-editor-wrapper'}
      onClick={handleLinkClick}
      onKeyDown={handleEnterPress}
      tabIndex={0}
      role={isDisabled ? 'button' : ''}
    >
      <label className={'text-editor-label'} htmlFor={'message'}>
        {label}
      </label>
      <RichTextEditor
        name={name}
        className={`RichTextEditor ${isDisabled ? 'disabled' : ''}`}
        readOnly={isDisabled}
        autoFocus={autoFocus}
        toolbarConfig={toolbarConfig}
        value={value}
        onChange={onChange}
        handleBeforeInput={handleBeforeInputRichTextEditor}
        handlePastedText={handlePastTextRichTextEditor}
        handleReturn={handleBeforeInputRichTextEditor}
        onBlur={onBlur}
        ref={editorContainerRef}
        customControls={[
          () => (
            <button
              className={'editor-toolbar-fix-button'}
              onClick={handleRichTextEditorFix}
              title={'Fix problematic characters from ChatGPT and other sources'}
            >
              Fix
            </button>
          )
        ]}
      />
      {(error || subText || symbolsCountText) && (
        <div
          // eslint-disable-next-line max-len
          className={`text-editor-under-text-wrapper ${!error && !subText && symbolsCountText ? 'justify-content-flex-end' : ''}`}
        >
          {(error || subText) && (
            <p className={`text-editor-sub-text ${error ? 'text-editor-sub-text-error' : ''}`}>{error || subText}</p>
          )}
          {symbolsCountText && <p className={'text-editor-symbols-count-text'}>{symbolsCountText}</p>}
        </div>
      )}
    </div>
  ) : (
    <div className={inputClasses}>
      {label && <label htmlFor={name}>{label + (isRequired ? '*' : '')}</label>}
      <input
        ref={inputRef}
        name={name}
        type={inputType}
        value={value || ''}
        onChange={inputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={isDisabled}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        maxLength={max}
        className={'input'}
      />
      {renderIcons()}
      {(error || subText || symbolsCountText) && (
        <div
          // eslint-disable-next-line max-len
          className={`input-under-text-wrapper ${!error && !subText && symbolsCountText ? 'justify-content-flex-end' : ''}`}
        >
          {(error || subText) && <p className={`input-sub-text ${error ? 'input-error' : ''}`}>{error || subText}</p>}
          {symbolsCountText && <p className={'input-symbols-count-text'}>{symbolsCountText}</p>}
        </div>
      )}
    </div>
  );
};

Input.propTypes = {
  type: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isApiError: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  setValue: PropTypes.func,
  subText: PropTypes.string,
  symbolsCountText: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  isRequired: PropTypes.bool,
  autoFocus: PropTypes.bool,
  isDisabled: PropTypes.bool,
  autoComplete: PropTypes.string,
  placeholder: PropTypes.string,
  initialStatus: PropTypes.number,
  Icon: PropTypes.object,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  max: PropTypes.number,
  rows: PropTypes.number,
  setCountryCode: PropTypes.func,
  excludeCountries: PropTypes.array,
  inputRef: PropTypes.object
};

export default Input;
